import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, BarChart, Bar, Cell } from "recharts";
import { toast } from "sonner";
import { Radio } from "lucide-react";

function num(v, d = 2) {
  const n = parseFloat(v ?? 0);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

export default function Trade() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSymbol = searchParams.get("symbol") || "NVDA";
  const [symbol, setSymbol] = useState(initialSymbol);
  const [input, setInput] = useState(initialSymbol);
  const [quote, setQuote] = useState(null);
  const [bars, setBars] = useState([]);
  const [macd, setMacd] = useState([]);
  const [loading, setLoading] = useState(false);

  // Order form state
  const [qty, setQty] = useState(1);
  const [side, setSide] = useState("buy");
  const [orderType, setOrderType] = useState("market");
  const [limitPrice, setLimitPrice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadData = async (sym) => {
    setLoading(true);
    try {
      const [q, b] = await Promise.all([
        api.get(`/market/quote?symbol=${sym}`),
        api.get(`/market/bars?symbol=${sym}&limit=120`),
      ]);
      setQuote(q.data);
      setBars(b.data.bars || []);
      setMacd(b.data.macd || []);
    } catch (err) {
      toast.error("Failed to load market data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(symbol);
    setSearchParams({ symbol });
  }, [symbol]);

  // Live tick price for the currently viewed symbol via 2-sec REST polling
  const [livePrice, setLivePrice] = useState(null);
  const [liveTime, setLiveTime] = useState(null);
  const liveConnected = livePrice != null;
  useEffect(() => {
    if (!symbol) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/market/quote?symbol=${symbol}`);
        if (!cancelled) {
          setLivePrice(r.data?.price);
          setLiveTime(Date.now());
        }
      } catch (_) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [symbol]);
  const displayPrice = livePrice ?? quote?.price;
  const prevClose = quote?.prev_close;
  const liveChange = displayPrice != null && prevClose ? displayPrice - prevClose : quote?.change;
  const liveChangePct = displayPrice != null && prevClose ? (liveChange / prevClose) * 100 : quote?.change_pct;
  const isPulsing = liveTime != null && Date.now() - liveTime < 1500;

  const chartData = useMemo(() => {
    return bars.map((b, i) => ({
      idx: i,
      time: new Date(b.t).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit" }),
      close: b.c,
      macd: macd[i]?.macd ?? null,
      signal: macd[i]?.signal ?? null,
      hist: macd[i]?.hist ?? null,
    }));
  }, [bars, macd]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        symbol,
        qty: parseFloat(qty),
        side,
        type: orderType,
        limit_price: orderType === "limit" ? parseFloat(limitPrice) : null,
      };
      const r = await api.post("/orders", body);
      toast.success(`Order ${r.data.status || "submitted"}: ${side.toUpperCase()} ${qty} ${symbol}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order failed");
    } finally {
      setSubmitting(false);
    }
  };

  const positive = (quote?.change || 0) >= 0;

  return (
    <div className="p-6 space-y-6" data-testid="trade-page">
      {/* Symbol search + quote header */}
      <div className="flex flex-wrap items-end gap-6">
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSymbol(input.toUpperCase());
          }}
        >
          <div>
            <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">SYMBOL</label>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value.toUpperCase())}
              data-testid="symbol-search-input"
              className="mt-1 w-32 bg-[#141415] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-lg font-bold"
            />
          </div>
          <button
            type="submit"
            data-testid="load-symbol-btn"
            className="bg-white text-black mono text-[10px] tracking-[0.22em] font-bold px-4 py-2 hover:bg-[#D4D4D8]"
          >
            LOAD →
          </button>
        </form>

        {quote && (
          <div className="flex items-baseline gap-4" data-testid="quote-header">
            <div className={`text-4xl font-black mono transition-colors ${isPulsing ? (liveChange >= 0 ? "text-[#00E676]" : "text-[#FF3B30]") : ""}`}>
              ${num(displayPrice)}
            </div>
            <div className={`mono ${liveChange >= 0 ? "gain" : "loss"}`} data-testid="quote-change">
              {liveChange >= 0 ? "+" : ""}{num(liveChange)} ({num(liveChangePct)}%)
            </div>
            <div className="mono text-xs text-[#71717A]">PREV CLOSE {num(prevClose)}</div>
            <div className="flex items-center gap-1.5 mono text-[10px]" data-testid="live-status">
              <Radio size={10} className={liveConnected ? "text-[#00E676] animate-pulse" : "text-[#71717A]"} />
              <span className={liveConnected ? "text-[#00E676]" : "text-[#71717A]"}>
                {liveConnected ? "LIVE" : "OFFLINE"}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Charts column */}
        <div className="lg:col-span-2 space-y-4">
          <div className="panel" data-testid="price-chart-panel">
            <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
              <div className="mono text-[11px] tracking-[0.22em]">{symbol} · 1H CLOSE</div>
              <div className="mono text-[10px] text-[#71717A]">{bars.length} BARS</div>
            </div>
            <div className="h-64 px-2">
              {loading || chartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-[#71717A] mono text-xs">LOADING CHART...</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 16, right: 20, left: 8, bottom: 8 }}>
                    <XAxis dataKey="idx" hide />
                    <YAxis stroke="#71717A" tick={{ fontFamily: 'JetBrains Mono', fontSize: 10, fill: '#71717A' }} domain={['auto','auto']} width={56} />
                    <Tooltip
                      contentStyle={{ background: '#141415', border: '1px solid #27272A', borderRadius: 0, fontFamily: 'JetBrains Mono', fontSize: 11 }}
                      labelFormatter={(idx) => chartData[idx]?.time}
                    />
                    <Line type="monotone" dataKey="close" stroke="#00E676" strokeWidth={2} dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="panel" data-testid="macd-chart-panel">
            <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
              <div className="mono text-[11px] tracking-[0.22em]">MACD · 12·26·9 · 1H</div>
              <div className="mono text-[10px] text-[#71717A]">
                MACD <span className="text-white">{num(macd[macd.length - 1]?.macd, 3)}</span>
                {"  "}SIG <span className="text-white">{num(macd[macd.length - 1]?.signal, 3)}</span>
              </div>
            </div>
            <div className="h-44 px-2">
              {chartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-[#71717A] mono text-xs">—</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 12, right: 20, left: 8, bottom: 8 }}>
                    <XAxis dataKey="idx" hide />
                    <YAxis stroke="#71717A" tick={{ fontFamily: 'JetBrains Mono', fontSize: 10, fill: '#71717A' }} width={56} />
                    <Tooltip
                      contentStyle={{ background: '#141415', border: '1px solid #27272A', borderRadius: 0, fontFamily: 'JetBrains Mono', fontSize: 11 }}
                      labelFormatter={(idx) => chartData[idx]?.time}
                    />
                    <ReferenceLine y={0} stroke="#52525B" strokeDasharray="2 2" />
                    <Line type="monotone" dataKey="macd" stroke="#00E676" strokeWidth={2} dot={false} isAnimationActive={false} />
                    <Line type="monotone" dataKey="signal" stroke="#FF3B30" strokeWidth={2} dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Order panel */}
        <div className="panel h-fit" data-testid="order-panel">
          <div className="px-4 py-3 border-b border-[#27272A]">
            <div className="mono text-[11px] tracking-[0.22em]">PLACE ORDER</div>
          </div>
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
              {["buy", "sell"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSide(s)}
                  data-testid={`side-${s}-btn`}
                  className={`py-2 mono text-[11px] tracking-[0.22em] font-bold ${
                    side === s
                      ? s === "buy" ? "bg-[#00E676] text-black" : "bg-[#FF3B30] text-white"
                      : "bg-[#141415] text-[#A1A1AA] hover:bg-[#1C1C1E]"
                  }`}
                >
                  {s.toUpperCase()}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
              {["market", "limit"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setOrderType(t)}
                  data-testid={`type-${t}-btn`}
                  className={`py-2 mono text-[10px] tracking-[0.22em] ${
                    orderType === t ? "bg-[#1C1C1E] text-white" : "bg-[#141415] text-[#A1A1AA]"
                  }`}
                >
                  {t.toUpperCase()}
                </button>
              ))}
            </div>

            <div>
              <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">QUANTITY</label>
              <input
                type="number"
                step="0.0001"
                min="0.0001"
                required
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                data-testid="order-qty-input"
                className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
              />
            </div>

            {orderType === "limit" && (
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">LIMIT PRICE</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={limitPrice}
                  onChange={(e) => setLimitPrice(e.target.value)}
                  data-testid="order-limit-input"
                  className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                />
              </div>
            )}

            <div className="border-t border-[#27272A] pt-3 space-y-1 mono text-xs">
              <div className="flex justify-between text-[#A1A1AA]">
                <span>Est. Notional</span>
                <span className="text-white">
                  ${num(parseFloat(qty || 0) * (orderType === "limit" ? parseFloat(limitPrice || 0) : (quote?.price || 0)))}
                </span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span>Current</span>
                <span className="text-white">${num(quote?.price)}</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-order-btn"
              className={`w-full py-3 mono text-xs tracking-[0.22em] font-bold disabled:opacity-50 ${
                side === "buy" ? "bg-[#00E676] text-black hover:bg-[#00C964]" : "bg-[#FF3B30] text-white hover:bg-[#D32F2F]"
              }`}
            >
              {submitting ? "SUBMITTING..." : `${side.toUpperCase()} ${qty || 0} ${symbol} →`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
